import React, { useContext, useEffect, useState } from "react"
import { Link, useRouteMatch } from "react-router-dom"
import moment from "moment-timezone"
import { Map, CreditCard, Info, ArrowRight, Mail, CheckCircle2 } from "lucide-react"

import { BookingContext } from "../../../utils/providers/isolated-booking"
import { db } from "../../../utils/firebase"

import styles from "./home.module.scss"

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

/**
 * Home / overview — the portal landing page.
 *
 * Surfaces the at-a-glance picture: a destination hero with a countdown, the
 * balance state with a progress bar and a pay CTA, quick links into the other
 * sections, and the agent's contact details.
 *
 * Times are pinned to Europe/London by the portal root (moment.tz.setDefault),
 * so the countdown here reflects UK time regardless of the client's device.
 */
export default function Home() {
	const { url } = useRouteMatch()

	const {
		bookingID,
		client,
		agent,
		reference,
		dateFromTimestamp,
		totalCost,
		paidByClient,
		outstanding,
		balanceDue,
		hotelsOrder,
	} = useContext(BookingContext)

	const [heroImage, setHeroImage] = useState("")

	// Pull a hero photo from the first hotel on the booking, if one exists.
	useEffect(() => {
		if (!bookingID || !hotelsOrder?.length) return
		let cancelled = false

		;(async () => {
			try {
				const hotelDoc = await db.doc(`bookings/${bookingID}/hotels/${hotelsOrder[0]}`).get()
				const resortID = hotelDoc.data()?.resortID
				if (!resortID) return

				const images = await db.collection(`hotels/${resortID}/images`).limit(1).get()
				if (!cancelled && !images.empty) {
					setHeroImage(images.docs[0].data().public_url)
				}
			} catch (err) {
				// Non-critical — the hero just falls back to its brand gradient.
				console.log("Home hero image load failed:", err)
			}
		})()

		return () => {
			cancelled = true
		}
	}, [bookingID, hotelsOrder])

	const isInFuture = moment(dateFromTimestamp, "X").valueOf() > moment().valueOf()
	const countdown = moment(dateFromTimestamp, "X").toNow(true)

	const total = Number(totalCost || 0)
	const paid = Number(paidByClient || 0)
	const owed = Number(outstanding || 0)
	const paidPct = total > 0 ? Math.min(100, Math.max(0, Math.round((paid / total) * 100))) : 0
	const settled = owed <= 0 && total > 0
	// balanceDue can be an empty object (provider's initial state), the number
	// 0, "Invalid date", or a real "DD/MM/YYYY" string — only render the last.
	const dueText = typeof balanceDue === "string" && balanceDue !== "Invalid date" ? balanceDue : null

	return (
		<div className={styles.home}>
			{/* Hero */}
			<section
				className={styles.hero}
				style={heroImage ? { backgroundImage: `url("${heroImage}")` } : undefined}>
				<div className={styles.heroOverlay} />
				<div className={styles.heroContent}>
					{reference ? <p className={styles.eyebrow}>Booking #{reference}</p> : null}
					{isInFuture ? (
						<h1>
							{client?.first_name ? `${client.first_name}, your` : "Your"} golf holiday is{" "}
							<span>{countdown}</span> away
						</h1>
					) : (
						<h1>Enjoy your holiday{client?.first_name ? `, ${client.first_name}` : ""}!</h1>
					)}
				</div>
			</section>

			{/* Balance */}
			<section className={styles.balance}>
				<div className={styles.balanceHead}>
					<h2>Your balance</h2>
					{settled ? (
						<span className={styles.paidBadge}>
							<CheckCircle2 size={15} />
							Paid in full
						</span>
					) : null}
				</div>

				<div className={styles.progress}>
					<div
						className={styles.progressFill}
						style={{ width: `${paidPct}%` }}
					/>
				</div>

				<div className={styles.balanceRows}>
					<div>
						<span className={styles.balanceLabel}>Paid so far</span>
						<span className={styles.balanceValue}>£{formatGbp(paid)}</span>
					</div>
					<div>
						<span className={styles.balanceLabel}>Total cost</span>
						<span className={styles.balanceValue}>£{formatGbp(total)}</span>
					</div>
					<div>
						<span className={styles.balanceLabel}>Outstanding</span>
						<span className={`${styles.balanceValue} ${owed > 0 ? styles.owed : ""}`}>£{formatGbp(owed)}</span>
					</div>
				</div>

				{!settled && (
					<div className={styles.balanceCta}>
						<div className={styles.dueNote}>{dueText ? <>Balance due by {dueText}</> : <>Pay any time towards your holiday</>}</div>
						<Link
							to={`${url}/payments`}
							className={styles.payButton}>
							Make a payment
							<ArrowRight size={17} />
						</Link>
					</div>
				)}
			</section>

			{/* Quick links */}
			<section className={styles.links}>
				<Link
					to={`${url}/itinerary`}
					className={styles.linkCard}>
					<span className={styles.linkIcon}>
						<Map size={20} />
					</span>
					<span className={styles.linkText}>
						<strong>Your itinerary</strong>
						<small>Hotels, tee times, transfers</small>
					</span>
					<ArrowRight
						className={styles.linkArrow}
						size={18}
					/>
				</Link>

				<Link
					to={`${url}/payments`}
					className={styles.linkCard}>
					<span className={styles.linkIcon}>
						<CreditCard size={20} />
					</span>
					<span className={styles.linkText}>
						<strong>Payments</strong>
						<small>Pay online &amp; view history</small>
					</span>
					<ArrowRight
						className={styles.linkArrow}
						size={18}
					/>
				</Link>

				<Link
					to={`${url}/checkin`}
					className={styles.linkCard}>
					<span className={styles.linkIcon}>
						<Info size={20} />
					</span>
					<span className={styles.linkText}>
						<strong>Useful info</strong>
						<small>Check-in times &amp; nearby</small>
					</span>
					<ArrowRight
						className={styles.linkArrow}
						size={18}
					/>
				</Link>
			</section>

			{/* Agent */}
			{(agent?.first_name || agent?.email) && (
				<section className={styles.agent}>
					<div className={styles.agentAvatar}>
						{agent.photo ? (
							<img
								src={agent.photo}
								alt={`${agent.first_name || ""} ${agent.last_name || ""}`.trim() || "Sales agent"}
							/>
						) : (
							(agent.first_name?.[0] || "") + (agent.last_name?.[0] || "") || "GHD"
						)}
					</div>
					<div className={styles.agentText}>
						<small>Your personal sales agent</small>
						<strong>
							{agent.first_name} {agent.last_name}
						</strong>
					</div>
					{agent.email ? (
						<a
							href={`mailto:${agent.email}`}
							className={styles.agentContact}>
							<Mail size={16} />
							<span>Email</span>
						</a>
					) : null}
				</section>
			)}
		</div>
	)
}
